import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { getProcurements } from '../../../services/dossier.service';
import { getContractTypes, type ContractType } from '../../../services/nomenclatoare.service';
import type { Procurement } from '../../../types';
import { Button, DateInput } from '../../common';
import { SearchableSelect } from '../../common/SearchableSelect';
import UserSelect from '../common/UserSelect';
import FileDropArea from '../common/FileDropArea';

interface Props {
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
  dossierId: number;
  projectId?: number;
  initialData?: any;
  bare?: boolean; // When true, renders only form fields (no container/title/buttons)
}

export interface ContractFormHandle {
  submit: () => void;
  isSubmitting: boolean;
  isValid: boolean;
}

const ContractForm = forwardRef<ContractFormHandle, Props>(({ onSubmit, onCancel, dossierId, projectId, initialData, bare = false }, ref) => {
  const toDateStr = (v: any) => v ? String(v).substring(0, 10) : '';
  const [contractTypes, setContractTypes] = useState<ContractType[]>([]);
  const [responsibleUserId, setResponsibleUserId] = useState<number | null>(
    initialData?.responsible_user_id ?? null
  );
  // Documente atașate la crearea unui contract nou. Se urcă după creare
  // (avem nevoie de id-ul contractului) — vezi ContracteSection.handleCreate.
  const [files, setFiles] = useState<File[]>([]);
  const [data, setData] = useState({
    procurement_id: initialData?.procurement_id || '',
    contract_type: initialData?.contract_type || '',
    contract_name: initialData?.contract_name || '',
    contract_number: initialData?.contract_number || '',
    contractor_name: initialData?.contractor_name || '',
    contract_date: toDateStr(initialData?.contract_date),
    contract_value: initialData?.contract_value || '',
    duration_months: initialData?.duration_months || '',
    start_date: toDateStr(initialData?.start_date),
    end_date: toDateStr(initialData?.end_date),
    notes: initialData?.notes || '',
    contract_object: initialData?.contract_object || '',
    eligible_expenses: initialData?.eligible_expenses || '',
    non_eligible_expenses: initialData?.non_eligible_expenses || '',
    detailed_budget: initialData?.detailed_budget || '',
    penalties: initialData?.penalties || ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [procurements, setProcurements] = useState<Procurement[]>([]);
  // #10: câmpurile opționale sunt ascunse implicit. Le desfacem automat la editare
  // dacă vreunul dintre ele conține deja o valoare, ca să nu ascundem date existente.
  const hasOptionalData = !!(
    initialData?.contract_number || initialData?.contractor_name || initialData?.contract_date ||
    initialData?.contract_value || initialData?.duration_months || initialData?.start_date ||
    initialData?.end_date || initialData?.notes || initialData?.contract_object ||
    initialData?.eligible_expenses || initialData?.non_eligible_expenses ||
    initialData?.detailed_budget || initialData?.penalties
  );
  const [showMore, setShowMore] = useState(hasOptionalData);

  useEffect(() => {
    getProcurements(dossierId).then(setProcurements).catch(console.error);
    getContractTypes().then(types => {
      const active = types.filter(t => t.is_active);
      setContractTypes(active);
      if (!initialData?.contract_type && active.length > 0) {
        setData(d => ({ ...d, contract_type: active[0].code }));
      }
    }).catch(console.error);
  }, [dossierId]);

  const handleSubmit = async () => {
    if (!data.contract_name) return;
    setSubmitting(true);
    try {
      await onSubmit({
        ...data,
        responsible_user_id: responsibleUserId,
        procurement_id: data.procurement_id ? parseInt(data.procurement_id as string) : null,
        contract_value: data.contract_value ? parseFloat(data.contract_value) : 0,
        duration_months: data.duration_months ? parseInt(data.duration_months) : null,
        eligible_expenses: data.eligible_expenses ? parseFloat(data.eligible_expenses) : null,
        non_eligible_expenses: data.non_eligible_expenses ? parseFloat(data.non_eligible_expenses) : null,
        _files: files,
      });
    } finally { setSubmitting(false); }
  };

  useImperativeHandle(ref, () => ({
    submit: handleSubmit,
    isSubmitting: submitting,
    isValid: !!data.contract_name,
  }));

  const inputClass = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500";

  const field = (label: string, key: string, type = 'text', placeholder = '') => {
    if (type === 'date') {
      return <DateInput label={label} value={(data as any)[key]} onChange={e => setData(d => ({ ...d, [key]: e.target.value }))} />;
    }
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <input type={type} value={(data as any)[key]} onChange={e => setData(d => ({ ...d, [key]: e.target.value }))}
          placeholder={placeholder} className={inputClass} />
      </div>
    );
  };

  const formFields = (
    <div className="space-y-4">
      {/* Câmpuri obligatorii / esențiale — mereu vizibile */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {procurements.length > 0 && (
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Dosar de achiziție</label>
            <SearchableSelect
              value={data.procurement_id}
              onChange={v => setData(d => ({ ...d, procurement_id: String(v) }))}
              placeholder="— Fără legătură —"
              options={procurements.map(p => ({ value: p.id, label: p.name }))}
            />
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tip contract</label>
          <SearchableSelect
            value={data.contract_type}
            onChange={v => setData(d => ({ ...d, contract_type: v as string }))}
            options={contractTypes.map(ct => ({ value: ct.code, label: ct.name }))}
          />
        </div>
        {field('Denumire contract *', 'contract_name', 'text', 'ex: Contract execuție lucrări DJ 107')}
        {projectId != null && (
          <UserSelect projectId={projectId} value={responsibleUserId} onChange={setResponsibleUserId} />
        )}
      </div>

      {/* Detalii suplimentare (opțional) — pliabil */}
      <div className="border-t border-gray-200 pt-3">
        <button
          type="button"
          onClick={() => setShowMore(v => !v)}
          className="flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          {showMore ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          Detalii suplimentare (opțional)
        </button>
        {showMore && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {field('Număr contract', 'contract_number', 'text', 'ex: 1234/2026')}
            {field('Contractor', 'contractor_name', 'text', 'ex: SC Construct SRL')}
            {field('Data contract', 'contract_date', 'date')}
            {field('Valoare (RON)', 'contract_value', 'number', '0.00')}
            {field('Durată (luni)', 'duration_months', 'number', 'ex: 24')}
            {field('Data început', 'start_date', 'date')}
            {field('Data sfârșit', 'end_date', 'date')}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Observații</label>
              <textarea value={data.notes} onChange={e => setData(d => ({ ...d, notes: e.target.value }))}
                rows={2} className={inputClass} />
            </div>
            {data.contract_type === 'finantare' && (
              <>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Obiectul contractului</label>
                  <textarea value={data.contract_object} onChange={e => setData(d => ({ ...d, contract_object: e.target.value }))}
                    rows={2} className={inputClass} />
                </div>
                {field('Cheltuieli eligibile (RON)', 'eligible_expenses', 'number')}
                {field('Cheltuieli neeligibile (RON)', 'non_eligible_expenses', 'number')}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bugetul detaliat al proiectului</label>
                  <textarea value={data.detailed_budget} onChange={e => setData(d => ({ ...d, detailed_budget: e.target.value }))}
                    rows={3} className={inputClass} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Penalități</label>
                  <textarea value={data.penalties} onChange={e => setData(d => ({ ...d, penalties: e.target.value }))}
                    rows={2} className={inputClass} />
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Documente — doar la contract NOU (la editare, documentele se gestionează
          din cardul contractului). Se urcă după creare, când avem id-ul. */}
      {!initialData && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Documente contract (opțional)</label>
          <FileDropArea files={files} onFilesChange={setFiles} id="contract-create-files" />
          {files.length > 0 && (
            <p className="text-xs text-gray-500 mt-1">{files.length} fișier{files.length > 1 ? 'e' : ''} — se atașează după crearea contractului.</p>
          )}
        </div>
      )}
    </div>
  );

  if (bare) return formFields;

  return (
    <div className="mb-4 p-5 bg-gray-50 rounded-lg border border-gray-200">
      <h4 className="font-semibold text-gray-800 mb-4 text-base">{initialData ? 'Editare contract' : 'Contract nou'}</h4>
      {formFields}
      <div className="flex gap-3 mt-4">
        <Button variant="primary" size="md" onClick={handleSubmit} disabled={submitting || !data.contract_name} isLoading={submitting}>
          {submitting ? 'Se salvează...' : initialData ? 'Salvează' : 'Creează contract'}
        </Button>
        <Button variant="secondary" size="md" onClick={onCancel}>Anulează</Button>
      </div>
    </div>
  );
});

ContractForm.displayName = 'ContractForm';
export default ContractForm;
